import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { DayButton, DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { format, startOfDay, startOfMonth } from "date-fns";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  deleteEmployeeHours,
  getEmployeeHours,
  saveEmployeeHours,
} from "../../services/employeeHoursService";

const toDateKey = (date) => format(date, "yyyy-MM-dd");
const toMonthKey = (date) => format(date, "yyyy-MM");

const EmployeeHoursModal = ({ show, onClose, employee }) => {
  const { t } = useTranslation();

  const today = useMemo(() => startOfDay(new Date()), []);
  const [month, setMonth] = useState(startOfMonth(today));
  const [selectedDate, setSelectedDate] = useState(null);
  const [entries, setEntries] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [hoursInput, setHoursInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const employeeId = employee?.id;
  const employeeName = `${employee?.first_name || ""} ${
    employee?.middle_name || ""
  } ${employee?.last_name || ""}`
    .replace(/\s+/g, " ")
    .trim();

  // work_date -> entry, for both the calendar labels and the editor prefill.
  const entriesByDate = useMemo(() => {
    const map = {};
    entries.forEach((entry) => {
      map[entry.work_date] = entry;
    });
    return map;
  }, [entries]);

  const fetchHours = useCallback(
    async (monthDate) => {
      if (!employeeId) return;
      setLoading(true);
      try {
        const res = await getEmployeeHours(employeeId, toMonthKey(monthDate));
        setEntries(res?.data?.entries || []);
        setTotalHours(res?.data?.total_hours || 0);
      } catch (error) {
        console.error("Error fetching employee hours:", error);
        setEntries([]);
        setTotalHours(0);
        toast.error(
          error?.response?.data?.error || t("Unable to load employee hours."),
        );
      } finally {
        setLoading(false);
      }
    },
    [employeeId, t],
  );

  // Reset to the current month each time the modal opens.
  useEffect(() => {
    if (!show) return;
    const currentMonth = startOfMonth(today);
    setMonth(currentMonth);
    setSelectedDate(null);
    setHoursInput("");
    setNoteInput("");
    fetchHours(currentMonth);
  }, [show, today, fetchHours]);

  const handleMonthChange = (nextMonth) => {
    setMonth(nextMonth);
    setSelectedDate(null);
    setHoursInput("");
    setNoteInput("");
    fetchHours(nextMonth);
  };

  const handleDaySelect = (date) => {
    if (!date) {
      setSelectedDate(null);
      setHoursInput("");
      setNoteInput("");
      return;
    }
    const day = startOfDay(date);
    const existing = entriesByDate[toDateKey(day)];
    setSelectedDate(day);
    setHoursInput(existing ? String(existing.hours) : "");
    setNoteInput(existing?.note || "");
  };

  const handleSave = async () => {
    if (!selectedDate) return;

    const parsedHours = Number(hoursInput);
    if (Number.isNaN(parsedHours) || parsedHours <= 0 || parsedHours > 24) {
      toast.error(t("Enter hours between 0 and 24."));
      return;
    }

    setSaving(true);
    try {
      await saveEmployeeHours({
        employee_id: employeeId,
        work_date: toDateKey(selectedDate),
        hours: parsedHours,
        note: noteInput || null,
      });
      toast.success(t("Hours saved"));
      await fetchHours(month);
    } catch (error) {
      console.error("Error saving employee hours:", error);
      toast.error(
        error?.response?.data?.error || t("Unable to save hours, try again."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const existing = selectedDate && entriesByDate[toDateKey(selectedDate)];
    if (!existing) return;

    setSaving(true);
    try {
      await deleteEmployeeHours(existing.id);
      toast.success(t("Hours removed"));
      setHoursInput("");
      setNoteInput("");
      await fetchHours(month);
    } catch (error) {
      console.error("Error deleting employee hours:", error);
      toast.error(
        error?.response?.data?.error || t("Unable to remove hours, try again."),
      );
    } finally {
      setSaving(false);
    }
  };

  // Show the logged hours under the day number, and ring days that have hours.
  // The ring is styled on the button rather than via modifiersStyles: those land
  // on the day <td>, and the calendar table uses border-collapse: collapse,
  // which makes browsers drop border-radius (the ring came out square).
  const CalendarDayButton = useCallback(
    (props) => {
      const dayDate = props?.day?.date;
      const entry = dayDate ? entriesByDate[toDateKey(dayDate)] : null;
      return (
        <DayButton
          {...props}
          style={{
            ...props.style,
            width: "38px",
            height: "38px",
            padding: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            border: entry ? "2px solid #16a34a" : undefined,
          }}
        >
          <span className="d-flex flex-column align-items-center lh-1">
            <span>{dayDate ? dayDate.getDate() : ""}</span>
            <span
              style={{
                fontSize: "0.55rem",
                fontWeight: 600,
                color: "#16a34a",
                minHeight: "0.7rem",
              }}
            >
              {entry ? `${Number(entry.hours)}h` : ""}
            </span>
          </span>
        </DayButton>
      );
    },
    [entriesByDate],
  );

  const existingForSelected =
    selectedDate && entriesByDate[toDateKey(selectedDate)];

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header className="d-flex justify-content-between align-items-center">
        <Modal.Title style={{ fontSize: "1.1rem" }}>
          {t("Employee Hours")}
          {employeeName ? ` - ${employeeName}` : ""}
        </Modal.Title>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="border-0 bg-transparent p-0"
          style={{
            fontSize: "1.6rem",
            lineHeight: 1,
            color: "#6c757d",
            cursor: "pointer",
          }}
        >
          &times;
        </button>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="text-muted" style={{ fontSize: "0.85rem" }}>
            {t("Select a date to log the hours worked.")}
          </span>
          <span className="fw-bold">
            {t("Total")}: {totalHours} {t("hrs")}
          </span>
        </div>

        <div className="row">
          <div className="col-md-7 d-flex justify-content-center">
            {loading ? (
              <div
                className="d-flex align-items-center"
                style={{ height: 320 }}
              >
                <Spinner animation="border" size="sm" />
              </div>
            ) : (
              <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={handleDaySelect}
                month={month}
                onMonthChange={handleMonthChange}
                disabled={{ after: today }}
                components={{ DayButton: CalendarDayButton }}
                showOutsideDays
                fixedWeeks
                weekStartsOn={1}
                style={{ margin: "0 auto", maxWidth: "340px" }}
              />
            )}
          </div>

          <div className="col-md-5">
            {selectedDate ? (
              <>
                <div className="mb-3">
                  <label className="col-form-label fw-bold">
                    {format(selectedDate, "dd MMM yyyy")}
                  </label>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {t("Hours")} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="24"
                    step="0.25"
                    value={hoursInput}
                    onChange={(e) => setHoursInput(e.target.value)}
                    placeholder={t("e.g. 8")}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t("Note")}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    maxLength={255}
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    placeholder={t("Optional")}
                  />
                </Form.Group>
                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? t("Saving...") : t("Save")}
                  </Button>
                  {existingForSelected ? (
                    <Button
                      variant="outline-danger"
                      onClick={handleDelete}
                      disabled={saving}
                    >
                      {t("Delete")}
                    </Button>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="text-muted mt-4" style={{ fontSize: "0.85rem" }}>
                {t("No date selected.")}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t("Close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EmployeeHoursModal;
